import React, { useEffect, useState, useRef } from "react";
import questionsData from "../../assets/questions.json";
import FAQCard from "../FAQCard";
import MobileCard from "./MobileCard";
import { useGetTotalStatsQuery } from "../../api/api";
import CustomerReview from "../CustomerReview";
import { autoFormatNumber } from "../../lib/utils";
import { Link, useOutletContext } from "react-router-dom";
import FAQCardMobile from "./FAQCardMobile";
import { AWS_CLOUD_FRONT_URL } from "../../lib/constants";
import MobileFeatureCard from "./MobileFeatureCard";
import { AnimatedCounter } from "../AnimatedCounter/AnimatedCounter";
import { motion, useInView } from "framer-motion";

interface Stats {
  totalCategories: number;
  totalUsers: number;
  totalProducts: number;
}
interface LayoutContext {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}
const baseStats = {
  totalCategories: 40,
  totalUsers: 100000,
  totalProducts: 300,
};
const MobileHomepage: React.FC = () => {
  const { ...getTotalStats } = useGetTotalStatsQuery();
  const { setLoading } = useOutletContext<LayoutContext>();
  const [displayedStats, setDisplayedStats] = useState<any>(baseStats);
  const height = window.innerHeight;
  const width = window.innerWidth;
  const customerQuestions = questionsData.ForCustomers.map(
    (item) => item.question
  );
  const vendorQuestions = questionsData.ForVendors.map((item) => item.question);
  const generalQuestions = questionsData.GeneralQuestions.map(
    (item) => item.question
  );
  const firstSectionRef = useRef(null);
  const secondSectionRef = useRef(null);
  const thirdSectionRef = useRef(null);
  const fourthSectionRef = useRef(null);
  const fifthSectionRef = useRef(null);

  const isFirstSectionInView = useInView(firstSectionRef, {
    once: true,
    amount: 0.2,
    margin: "-50px 0px -50px 0px",
  });
  const isSecondSectionInView = useInView(secondSectionRef, {
    once: true,
    amount: 0.2,
    margin: "-50px 0px -50px 0px",
  });
  const isThirdSectionInView = useInView(thirdSectionRef, {
    once: true,
    amount: 0.2,
    margin: "-50px 0px -50px 0px",
  });
  const isFourthSectionInView = useInView(fourthSectionRef, {
    once: true,
    amount: 0.2,
    margin: "-50px 0px -50px 0px",
  });
  const isFifthSectionInView = useInView(fifthSectionRef, {
    once: true,
    amount: 0.2,
    margin: "-50px 0px -50px 0px",
  });

  const containerVariants = {
    hidden: {
      opacity: 0,
      y: 40,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 50,
        damping: 20,
        mass: 1.2,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 70,
        damping: 25,
        mass: 0.8,
      },
    },
  };

  const containerVariantsFromTopToBottom = {
    hidden: {
      opacity: 0,
      y: -40,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 50,
        damping: 20,
        mass: 1.2,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  useEffect(() => {
    if (getTotalStats?.data && getTotalStats.data?.data?.averageRating) {
      setLoading(false);
      const totalCategories = getTotalStats?.data?.data?.totalCategories;
      const totalUsers = getTotalStats?.data?.data?.totalUsers;
      const totalProducts = getTotalStats?.data?.data?.totalProducts;
      setDisplayedStats((prv: Stats) => {
        return {
          totalCategories:
            totalCategories >= prv.totalCategories
              ? totalCategories
              : prv.totalCategories,
          totalProducts:
            totalProducts >= prv.totalProducts
              ? totalProducts
              : prv.totalProducts,
          totalUsers:
            totalUsers >= prv.totalUsers ? totalUsers : prv.totalUsers,
        };
      });
    }
  }, [getTotalStats.data]);

  return (
    <div className="w-full min-h-max text-white overflow-hidden">
      <motion.div
        ref={firstSectionRef}
        initial="hidden"
        animate={isFirstSectionInView ? "visible" : "hidden"}
        variants={containerVariantsFromTopToBottom}
        className="relative bg-[#008600] px-[5vw] pt-4 z-10 flex flex-col flex-grow rounded-b-3xl justify-center items-center"
        style={{
          background:
            "radial-gradient(circle at 60% 100%, #C6F880, #008000, #008000 )",
        }}
      >
        <h1 className="font-helveticaBold text-3xl xs:text-[5vw] text-center">
          Your One-stop Shop for African Groceries and Catering Services
        </h1>
        {/* <p className="text-sm xs:text-[1vw] mt-4 text-center">
          Enjoy a vast selection of groceries, home-made dishes, and fast food from top restaurants, all delivered to your door.
        </p> */}
        <p className="font-normal text-base xs:text-[3vw] mt-4 text-center">
          Get our app for faster delivery, exclusive offers!
        </p>
        <div className="flex mt-5 gap-x-4 ">
          <a
            href="https://play.google.com/store/apps/details?id=com.afrocart.mobile"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={`${AWS_CLOUD_FRONT_URL}/PlayStoreBadge.png`}
              alt="play store badge"
              className="w-28 h-9 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110  duration-300"
            />
          </a>
          <a
            href="https://apps.apple.com/us/app/afrocart/id6636529260"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={`${AWS_CLOUD_FRONT_URL}/AppStoreBadge.png`}
              alt="play store badge"
              className="w-28 h-9 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110  duration-300"
            />
          </a>
        </div>
        <img
          src={`${AWS_CLOUD_FRONT_URL}/mobiles.webp`}
          alt="play store badge"
          className="w-[95%] self-center"
        />
        <CustomerReview
          averageRating={autoFormatNumber(
            getTotalStats.data?.data?.averageRating || 0
          )}
          totalRatingCount={autoFormatNumber(
            getTotalStats.data?.data?.totalRatingCount || 0
          )}
          isLoading={getTotalStats.isLoading}
        />
      </motion.div>
      <div className="h-[140px]">
        <img
          src="./Images/HomepageImages/Dotted_Grid_Mobile.png"
          alt="Grid with gray dots"
          className="relative bottom-20"
        />
      </div>
      <motion.div
        ref={secondSectionRef}
        initial="hidden"
        animate={isSecondSectionInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="relative bottom-20 p-4 flex flex-col space-y-4"
      >
        <motion.div variants={itemVariants}>
          <MobileFeatureCard
            icon="/Images/HomepageImages/cateringicon.png"
            title="Catering Services"
            description="Delicious catering tailored for every occasion, delivered fresh and on time."
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <MobileFeatureCard
            icon="/Images/HomepageImages/Shopping_bag.png"
            title="Groceries"
            description="Authentic African groceries, delivered fresh to your door."
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <MobileFeatureCard
            icon="/Images/HomepageImages/Briefcase.png"
            title="Services"
            description="Tailored solutions to meet your needs."
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <MobileFeatureCard
            icon="/Images/HomepageImages/Wallet.png"
            title="Secured Payment"
            description="Seamless and secure payments, guaranteed with every purchase."
          />
        </motion.div>
      </motion.div>
      <motion.div
        ref={thirdSectionRef}
        initial="hidden"
        animate={isThirdSectionInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="relative bottom-20 flex flex-col font-helveticaBold text-black mt-[3%]"
      >
        <h1 className="p-4 text-3xl xs:text-[5vw]">Download our Mobile App</h1>
        <div className="flex p-4 pb-0 justify-between xs:justify-around space-x-3 mt-4 text-xl">
          <AnimatedCounter
            value={displayedStats.totalUsers}
            label="Global Active Users"
            isMobile={true}
          />

          <AnimatedCounter
            value={displayedStats.totalCategories}
            label="Categories"
            isMobile={true}
          />
          <AnimatedCounter
            value={displayedStats.totalProducts}
            label="Products"
            isMobile={true}
          />
        </div>
        <div className="flex justify-center">
          <img
            src={`${AWS_CLOUD_FRONT_URL}/DownloadIconAndroid.png`}
            alt="DownloadIcons"
            className="-mt-10 object-fill"
          />
        </div>
        <div className="flex items-center justify-center -mt-[10vw] p-4 gap-x-4">
          <a
            href="https://play.google.com/store/apps/details?id=com.afrocart.mobile"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={`${AWS_CLOUD_FRONT_URL}/PlayStoreBadge.png`}
              alt="Play store download badge"
              className="w-36 h-12 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110  duration-300  rounded-md"
            />
          </a>
          <a
            href="https://apps.apple.com/us/app/afrocart/id6636529260"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={`${AWS_CLOUD_FRONT_URL}/AppStoreBadge.png`}
              alt="App store download badge"
              className="w-36 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110  duration-300  rounded-md"
            />
          </a>
        </div>
      </motion.div>
      <motion.div
        ref={fourthSectionRef}
        initial="hidden"
        animate={isFourthSectionInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="relative bottom-20 text-black flex flex-col p-4 space-y-4 mt-10"
      >
        <h1 className="font-helveticaBold text-3xl xs:text-[5vw]">
          Best Quality, Healthy & Fresh Groceries
        </h1>
        <p className="text-sm xs:text-[3vw] font-helvetica tracking-wide text-customGrayBase leading-tight">
          We take pride in delivering only the finest groceries, directly from
          trusted sources. Here's why customers love shopping with us:
        </p>
        <div className="space-y-4 mb-4">
          <section className="flex items-start justify-start text-customGreenColor">
            <img
              src="/Images/HomepageImages/ThirdSection/check-square.png"
              alt="square checkbox"
              className="w-4 xs:w-[4vw] h-4 xs:h-[4vw] mr-1 mt-1"
            />
            <div className="flex flex-col">
              <p className="text-base xs:text-[3vw] font-helveticaMedium tracking-wide">
                Premium Services You Can Trust
              </p>
              <p className="text-sm xs:text-[3vw] font-helveticaMedium tracking-wide text-customGrayBase">
                Enjoy faster deliveries and top-tier customer support every step
                of the way.
              </p>
            </div>
          </section>
          <section className="flex items-start justify-start text-customGreenColor">
            <img
              src="/Images/HomepageImages/ThirdSection/check-square.png"
              alt="square checkbox"
              className="w-4 xs:w-[4vw] h-4 xs:h-[4vw] mr-1 mt-1 mt-1"
            />
            <div className="flex flex-col">
              <p className="text-base xs:text-[3vw]  font-helveticaMedium tracking-wide">
                Convenient and Easy Ordering
              </p>
              <p className="text-sm xs:text-[3vw] font-helveticaMedium tracking-wide text-customGrayBase">
                Our user-friendly app makes it easy to browse, select, and
                receive your groceries.
              </p>
            </div>
          </section>
          <section className="flex items-start justify-start text-customGreenColor">
            <img
              src="/Images/HomepageImages/ThirdSection/check-square.png"
              alt="square checkbox"
              className="w-4 xs:w-[4vw] h-4 xs:h-[4vw] mr-1 mt-1"
            />
            <div className="flex flex-col">
              <p className="text-base xs:text-[3vw] font-helveticaMedium tracking-wide">
                100% Organic & Natural Products
              </p>
              <p className="text-sm xs:text-[3vw] font-helveticaMedium tracking-wide text-customGrayBase">
                All our products are sourced to ensure you receive the freshest,
                most natural ingredients.
              </p>
            </div>
          </section>
          <section className="flex items-start justify-start text-customGreenColor">
            <img
              src="/Images/HomepageImages/ThirdSection/check-square.png"
              alt="square checkbox"
              className="w-4 xs:w-[4vw] h-4 xs:h-[4vw] mr-1 mt-1"
            />
            <div className="flex flex-col">
              <p className="text-base xs:text-[3vw] font-helveticaMedium tracking-wide">
                Exclusive Deals for App Users
              </p>
              <p className="text-sm xs:text-[3vw] font-helveticaMedium tracking-wide text-customGrayBase">
                Unlock special discounts and offers when you shop through our
                mobile app.
              </p>
            </div>
          </section>
        </div>
        <img
          className="pt-10"
          src={`${AWS_CLOUD_FRONT_URL}/CustomerWithGroceryInHand.png`}
          alt="Customer with grocery in hand"
        />
      </motion.div>
      <motion.div
        ref={fifthSectionRef}
        initial="hidden"
        animate={isFifthSectionInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="relative bottom-12 flex flex-col space-y-3 text-black p-4"
      >
        <h1 className="font-helveticaBold text-3xl">
          What Our Customers <br /> And Vendors Are Saying
        </h1>
        <div className="h-full">
          <div className="flex overflow-x-auto space-x-4">
            <MobileCard
              title="Grocery Buyer"
              description="“Afrocart has brought me closer to my roots! I love being able to order fresh produce and spices from Ghana that I couldn't find anywhere else in Canada. The delivery is always on time, and the selection is unbeatable!”"
              name="Naomi O."
              city="Toronto"
              backgroundColor="#E7FFFF"
            />

            <MobileCard
              title="Catering Customer"
              description="“We ordered catering from Afrocart for my sister's wedding, and it was a hit! The jollof rice, suya, and egusi soup tasted just like home. The guests were raving about the food, and I can't thank Afrocart enough for bringing a taste of Nigeria to our celebration!”"
              name="Fatima M."
              city="Montreal"
              backgroundColor="#FFFBE7"
            />
            <MobileCard
              title="Modern Nigerian Cuisine"
              description="“Afrocart has been a fantastic partner for our business! Through the platform, we've connected with more customers who appreciate modern Nigerian dishes right here in Canada. It's been an incredible opportunity to showcase our unique flavors and grow our reach.”"
              name="Afrospyce Kitchen."
              city="Hamilton"
              backgroundColor="#FBE7FF"
            />
            <MobileCard
              title="Grocery Shop"
              description="“Thanks to Afrocart, we've been able to bring authentic African groceries to a larger audience across Toronto. The platform simplifies our connection with customers, making it easier for them to find and enjoy the products they love.”"
              name="Unbeatable Grace African Shop Ltd."
              city="Toronto"
              backgroundColor="#F0FFE7"
            />
          </div>
        </div>
      </motion.div>
      <div className="relative flex text-black flex-col p-4">
        <h1 className="font-helveticaBold text-3xl xs:text-[5vw]">
          Got Questions? <br /> We've Got Answers!
        </h1>
        <p className="text-customGrayDark font-helveticaMedium leading-tight mt-4">
          Whether you're looking to place an order or become a vendor, find all
          your answers in our FAQ section
        </p>

        <div className="w-full mt-6">
          <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide px-4">
            <div className="flex gap-3 py-1">
              <div className="snap-center">
                <FAQCardMobile
                  heading="For Customers"
                  Questions={customerQuestions}
                  linkTo="/faqs?customers"
                />
              </div>

              <div className="snap-center">
                <FAQCardMobile
                  heading="For Vendors"
                  Questions={vendorQuestions}
                  linkTo="/faqs?vendors"
                />
              </div>

              <div className="snap-center">
                <FAQCardMobile
                  heading="General Questions"
                  Questions={generalQuestions}
                  linkTo="/faqs?general-questions"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 px-2 text-black">
          <section className="relative bg-customDivColor rounded-2xl p-6">
            {/* Image container */}
            <div className="flex justify-center mb-4">
              <img
                src={`${AWS_CLOUD_FRONT_URL}/CustomerShopping.png`}
                alt="Customer Shopping"
                className="w-48 xs:w-56 h-auto -mt-12"
              />
            </div>

            {/* Content container */}
            <div className="flex flex-col items-center text-center">
              <h2 className="font-helveticaBold text-xl xs:text-2xl">
                Become a Vendor
              </h2>
              <p className="mt-3 font-normal text-black text-sm xs:text-base leading-tight">
                Grow your business, showcase your products, and reach more
                customers with Afrocart's dedicated vendor platform. Apply today
                and unlock new opportunities!
              </p>
              <Link
                to="/signup"
                className="bg-greenButtonColor text-white rounded-md text-sm xs:text-base mt-4 px-6 py-2.5 w-full max-w-[200px]"
              >
                Join as a Vendor
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
export default MobileHomepage;
